import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  const quickFacts = [
    { icon: 'ri-calendar-line', label: 'Established', value: '2020' },
    { icon: 'ri-map-pin-line', label: 'Location', value: 'Aurora, CO' },
    { icon: 'ri-user-heart-line', label: 'Patients Served', value: 'Adults 18+' },
    { icon: 'ri-hospital-line', label: 'Care Models', value: 'In-office, Telehealth, House Calls' },
  ];

  const conditions = [
    'Adults aged 18 and older',
    'Geriatric patients requiring specialized care',
    'Individuals with anxiety and mood disorders',
    'Patients managing bipolar disorder',
    'Those experiencing PTSD and trauma',
    'Individuals with schizophrenia and psychotic disorders',
    'Patients with dementia and cognitive concerns',
    'Those with neurodevelopmental disorders',
    'Individuals managing substance use disorders',
    'Anyone seeking compassionate mental health support',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            About Solutions Healthcare LLC
          </motion.h1>
          <motion.p
            className="text-white/90 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> About Us
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-beige-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Sidebar - Quick Facts */}
            <motion.div
              className="lg:sticky lg:top-24 h-fit"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-neutral-gray mb-6">Quick Facts</h3>
                <div className="space-y-6">
                  {quickFacts.map((fact, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <i className={`${fact.icon} text-2xl text-primary`}></i>
                      </div>
                      <div>
                        <p className="text-sm text-neutral-gray/60 font-semibold uppercase tracking-wide">
                          {fact.label}
                        </p>
                        <p className="text-neutral-gray font-medium mt-1">
                          {fact.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Our Purpose */}
              <motion.div
                className="mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-bold text-neutral-gray mb-6 font-serif">
                  Our Purpose
                </h2>
                <div className="space-y-4 text-lg text-neutral-gray/80 leading-relaxed">
                  <p>
                    Solutions Healthcare LLC was founded with a clear mission: to provide accessible, compassionate, and evidence-based mental health care to adults and seniors in Aurora, Colorado and the surrounding communities.
                  </p>
                  <p>
                    We understand that seeking mental health support can be challenging. That's why we've created a welcoming environment where patients feel heard, respected, and empowered throughout their treatment journey. Our experienced clinicians are dedicated to delivering personalized care that addresses each individual's unique needs and circumstances.
                  </p>
                  <p>
                    At Solutions Healthcare LLC, we believe that quality mental health care should be accessible to everyone. We accept most major insurance plans and offer flexible payment options to ensure that financial barriers don't prevent anyone from receiving the care they need.
                  </p>
                  <p>
                    Our comprehensive approach combines psychiatric evaluations, medication management, mental health therapy, and ongoing support to help our patients achieve lasting wellness. Whether you're managing a chronic condition or seeking help for the first time, we're here to support you every step of the way.
                  </p>
                </div>
              </motion.div>

              {/* Meet the Team */}
              <motion.div
                className="mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-neutral-gray mb-8">
                  Meet the Team
                </h2>
                <div className="bg-white rounded-2xl p-8 border border-primary/10 shadow-sm">
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src="https://static.readdy.ai/image/97be066482875b82b2fddabc195c0943/ee43f8a138006738e76a0a9a3c3d2c2f.jpeg"
                        alt="Risikat Sanusi - PMHNP-BC"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-neutral-gray mb-2">
                        Risikat Sanusi - PMHNP-BC
                      </h3>
                      <p className="text-primary font-semibold mb-4 text-lg">
                        Mental Health Consultant and Provider
                      </p>
                      <p className="text-neutral-gray/80 leading-relaxed">
                        Risi Sanusi is a mental health provider with over 20 years of nursing experience. She brings a wealth of experience from a variety of clinical settings and patient demographics to her practice. Her commitment to you is unwavering- to uphold the highest standards of care, deliver personalized care, result-driven solutions, and nurture positive relationships with every individual that she meets. Let's embark on this journey together to improve your mental health.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Who We Serve */}
              <motion.div
                className="mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-neutral-gray mb-6">
                  Who We Serve
                </h2>
                <p className="text-lg text-neutral-gray/80 leading-relaxed mb-6">
                  Our clinic specializes in providing mental health care to a diverse range of patients, including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {conditions.map((condition, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-primary text-xl mt-1 flex-shrink-0"></i>
                      <span className="text-neutral-gray/80">{condition}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Our Commitment */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-neutral-gray mb-6">
                  Our Commitment
                </h2>
                <div className="space-y-4 text-lg text-neutral-gray/80 leading-relaxed">
                  <p>
                    We are committed to providing the highest standard of psychiatric care in a compassionate, non-judgmental environment. Our team stays current with the latest research and treatment modalities to ensure our patients receive evidence-based care that produces real results.
                  </p>
                  <div className="bg-white border-l-4 border-primary p-6 rounded-r-lg my-8 shadow-sm">
                    <p className="text-xl font-serif italic text-neutral-gray">
                      "Mental health is not a destination, but a process. It's about how you drive, not where you're going. We're here to support you on that journey with expertise, compassion, and unwavering dedication to your wellness."
                    </p>
                    <p className="text-sm text-neutral-gray/60 mt-4 font-semibold">
                      — Solutions Healthcare LLC Team
                    </p>
                  </div>
                  <p>
                    Our flexible care delivery options—including in-office visits, telepsychiatry, and house calls—ensure that you can access quality mental health care in the setting that works best for your lifestyle and needs. We're here to meet you where you are, both literally and figuratively.
                  </p>
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div
                className="mt-12 bg-white rounded-2xl p-8 text-center shadow-sm"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-neutral-gray mb-4">
                  Ready to Begin Your Journey?
                </h3>
                <p className="text-neutral-gray/70 mb-6">
                  Take the first step toward better mental health today.
                </p>
                <Link
                  to="/appointment"
                  className="inline-flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Schedule an Appointment</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;